import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AddBiddersDialog — creator-only tool to bring more bidders into an
 * already-running auction. Late-joiners can quote from the current round.
 * The auction's blind-anonymity is preserved: existing bidders are not told.
 */
export const AddBiddersDialog = ({ auctionId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [busy, setBusy] = useState(false);

  const addEmail = () => {
    const e = input.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Invalid email");
      return;
    }
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setInput("");
  };

  const remove = (e) => setEmails(emails.filter((x) => x !== e));

  const submit = async () => {
    if (emails.length === 0) {
      toast.error("Add at least one email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${auctionId}/add-bidders`, { emails });
      const added = (data.added || []).length;
      const skipped = (data.skipped || []).length;
      const errored = (data.errors || []).length;
      const parts = [`Added ${added}`];
      if (skipped) parts.push(`${skipped} already invited`);
      if (errored) parts.push(`${errored} rejected (disqualified)`);
      if (added > 0) toast.success(parts.join(" · ") + ". Invite emails sent.");
      else toast.error(parts.join(" · "));
      // Show any per-email errors verbatim so the admin can act
      for (const err of (data.errors || [])) {
        toast.error(`${err.email}: ${err.reason || "Failed"}`);
      }
      setEmails([]);
      setInput("");
      if (added > 0) {
        setOpen(false);
        onChange?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add bidders");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-none border-black h-11 btn-sharp"
          data-testid="open-add-bidders-dialog"
        >
          <UserPlus size={14} weight="bold" className="mr-2" /> Add Bidders
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg" data-testid="add-bidders-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add bidders mid-auction</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-700 leading-relaxed">
          New bidders can join at any point while this auction is open. They&apos;ll see the current round target and can quote right away.
          Existing bidders are <strong>not</strong> notified — blind anonymity is preserved.
        </p>

        <div className="space-y-3">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold">
            Bidder emails to add
          </div>
          <div className="flex gap-2">
            <Input
              type="email"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="carrier@shipping.com"
              className="rounded-none border-black/20 h-11"
              data-testid="add-bidder-email-input"
              disabled={busy}
            />
            <Button
              type="button"
              onClick={addEmail}
              disabled={busy}
              className="rounded-none bg-[#0B1929] hover:bg-[#132639] text-white h-11 px-4"
              data-testid="add-bidder-email-add"
            >
              Add
            </Button>
          </div>
          {emails.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="add-bidder-list">
              {emails.map((e) => (
                <span key={e} className="inline-flex items-center gap-2 border border-black/20 px-2.5 py-1 text-xs font-mono-plex">
                  {e}
                  <button type="button" onClick={() => remove(e)} disabled={busy} title="Remove">
                    <X size={12} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[11px] text-neutral-500 leading-relaxed">
            Already-signed-up users get a direct auction link. Brand-new emails get an invite email with a signup link.
            Previously disqualified bidders are automatically rejected.
          </p>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || emails.length === 0}
            className="rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold btn-sharp"
            data-testid="add-bidders-confirm"
          >
            {busy ? "Adding..." : `Add ${emails.length || ""} bidder${emails.length === 1 ? "" : "s"}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
